import { FormProvider, useForm, useFieldArray } from "react-hook-form";
import { useEffect } from "react";
import DetailsSection from "./DetailsSection";
import TypeSection from "./TypeSection";
import FacilitiesSection from "./FacilitiesSection";
import GuestsSection from "./GuestsSection";
import ImagesSection from "./ImagesSection";
import { HotelType } from "../../../../backend/src/shared/types";

export type HotelFormData = {
  destId: string;
  serpId: string;
  name: string;
  city: string;
  country: string;
  description: string;
  type: string[];
  pricePerNight: number;
  starRating: number;
  facilities: string[];
  imageFiles: FileList;
  imageUrls: string[];
  adultCount: number;
  childCount: number;
  packageType: string;
  longitude: string;
  latitude: string;
  cancellationType: string;
  tripAdvisorRating: number;
  tripRating: number;
  depositPrice: number;
  whatsExciting: { value: string }[];
  faqQuestions: { value: string }[];
  faqAnswers: { value: string }[];
  roomsAndBoard: string;
  cancellation: string;
  availability: string;
  metaTitle: string;
  metaDescription: string;
  showHotel: boolean;
  destinationCode: string;
  dealHighlights: { value: string }[];
};

type Props = {
  hotel?: HotelType;
  onSave: (hotelFormData: FormData) => void;
  isLoading: boolean;
};

const ManageHotelForm = ({ onSave, isLoading, hotel }: Props) => {
  const formMethods = useForm<HotelFormData>({
    defaultValues: {
      whatsExciting: [{ value: '' }],
      faqQuestions: [{ value: '' }],
      faqAnswers: [{ value: '' }],
      roomsAndBoard: '',
      availability: '',
      cancellation: '',
      metaTitle: '',
      metaDescription: '',
      destinationCode: '',
      dealHighlights: [{ value: '' }]
    },
  });

  const { handleSubmit, reset, control } = formMethods;
  const { replace: replaceExcitingFields } = useFieldArray({
    control,
    name: "whatsExciting",
  });

  const { replace: replaceFaqFields } = useFieldArray({
    control,
    name: "faqQuestions",
  });

  const { replace: replaceFaqAnsFields } = useFieldArray({
    control,
    name: "faqAnswers",
  });

  const { replace: replaceDealHighlightFields } = useFieldArray({
    control,
    name: "dealHighlights",
  });

  useEffect(() => {
    if (hotel) {
      const formattedHotel = {
        ...hotel,
        whatsExciting: hotel.whatsExciting.length
          ? hotel.whatsExciting.map((item) => ({
              value: typeof item === 'string' ? item : item.value || '',
            }))
          : [{ value: '' }],
        faqQuestions: hotel.faqQuestions.length
          ? hotel.faqQuestions.map((item) => ({
              value: typeof item === 'string' ? item : item.value || '',
            }))
          : [{ value: '' }],
        faqAnswers: hotel.faqAnswers.length
          ? hotel.faqAnswers.map((item) => ({
              value: typeof item === 'string' ? item : item.value || '',
            }))
          : [{ value: '' }],
        dealHighlights: hotel.dealHighlights.length
          ? hotel.dealHighlights.map((item) => ({
              value: typeof item === 'string' ? item : item.value || '',
          }))
          : [{ value: '' }],
      };
  
      reset(formattedHotel);

      replaceExcitingFields(formattedHotel.whatsExciting);
      replaceFaqFields(formattedHotel.faqQuestions);
      replaceFaqAnsFields(formattedHotel.faqAnswers);
      replaceDealHighlightFields(formattedHotel.dealHighlights);
    }
  }, [hotel, reset, replaceExcitingFields, replaceFaqFields, replaceFaqAnsFields]);  

  const onSubmit = handleSubmit((formDataJson: HotelFormData) => {
    const formData = new FormData();
    if (hotel) {
      formData.append("hotelId", hotel._id);
    }
    formData.append("name", formDataJson.name);
    formData.append("city", formDataJson.city);
    formData.append("destId", formDataJson.destId);
    formData.append("serpId", formDataJson.serpId);
    formData.append("country", formDataJson.country);
    formData.append("description", formDataJson.description);
    formData.append("pricePerNight", formDataJson.pricePerNight.toString());
    formData.append("depositPrice", formDataJson.depositPrice.toString());
    formData.append("tripAdvisorRating", formDataJson.tripAdvisorRating.toString());
    formData.append("tripRating", formDataJson.tripRating.toString());
    formData.append("packageType", formDataJson.packageType.toString());
    formData.append("cancellationType", formDataJson.cancellationType.toString());
    formData.append("longitude", formDataJson.longitude.toString());
    formData.append("latitude", formDataJson.latitude.toString());
    formData.append("starRating", formDataJson.starRating.toString());
    formData.append("adultCount", formDataJson.adultCount.toString());
    formData.append("childCount", formDataJson.childCount.toString());
    formData.append("roomsAndBoard", formDataJson.roomsAndBoard);
    formData.append("cancellation", formDataJson.cancellation);
    formData.append("availability", formDataJson.availability);
    formData.append("metaTitle", formDataJson.metaTitle);
    formData.append("metaDescription", formDataJson.metaDescription);
    formData.append("showHotel", formDataJson.showHotel.toString());
    formData.append("destinationCode", formDataJson.destinationCode);

    formDataJson.facilities.forEach((facility, index) => {
      formData.append(`facilities[${index}]`, facility);
    });

    formDataJson.type.forEach((type, index) => {
      formData.append(`type[${index}]`, type);
    });

    if (formDataJson.imageUrls) {
      formDataJson.imageUrls.forEach((url, index) => {
        formData.append(`imageUrls[${index}]`, url);
      });
    }

    if (formDataJson.whatsExciting) {
      formDataJson.whatsExciting.forEach((item, index) => {
        formData.append(`whatsExciting[${index}]`, item.value);
      });
    }

    if (formDataJson.faqQuestions) {
      formDataJson.faqQuestions.forEach((item, index) => {
        formData.append(`faqQuestions[${index}]`, item.value);
      });
    }

    if (formDataJson.faqAnswers) {
      formDataJson.faqAnswers.forEach((item, index) => {
        formData.append(`faqAnswers[${index}]`, item.value);
      });
    }

    if (formDataJson.dealHighlights) {
      formDataJson.dealHighlights.forEach((item, index) => {
        formData.append(`dealHighlights[${index}]`, item.value);
      });
    }

    Array.from(formDataJson.imageFiles).forEach((imageFile) => {
      formData.append(`imageFiles`, imageFile);
    });

    onSave(formData);
  });

  return (
    <FormProvider {...formMethods}>
      <form className="flex flex-col gap-10" onSubmit={onSubmit}>
        <DetailsSection isEditMode={!!hotel} />
        <TypeSection />
        <FacilitiesSection />
        <GuestsSection />
        <ImagesSection />
        <span className="flex justify-center">
          <button
            disabled={isLoading}
            type="submit"
            className="px-6 py-2 border text-halo-pink text-md rounded uppercase font-[500] border-halo-pink hover:bg-halo-pink hover:text-white transition-colors duration-200"
            aria-label="Save"
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </span>
      </form>
    </FormProvider>
  );
};

export default ManageHotelForm;
